import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../lib/api.js";
import { addDays, datesCovered, today } from "../../lib/dates.js";
import { bandsForRow, spanAria, spanMeta } from "../../lib/span.js";
import { useSession } from "../../lib/session.js";
import { foregroundOn, initialOf, tintOf } from "../../tokens/palette.js";
import type { Occurrence } from "../../lib/types.js";
import { DesktopChrome } from "./DesktopChrome.js";

/*
 * The cell's content budget, measured rather than guessed.
 *
 * 132px of cell, less 14px of padding, is 118px. The date numeral and its gap
 * take 19, leaving 99. A slot — a band or a chip — is 21px, and the "+N more"
 * line costs 17 of its own:
 *
 *   4 slots alone      84 <= 99
 *   3 slots + overflow 80 <= 99   (4 + overflow would be 101 — over)
 *
 * So exceeding the budget drops two, not one. Bands and chips draw from the
 * same pool because they are the same height and stack in the same column.
 */
const SLOTS_ALONE = 4;
const SLOTS_WITH_MORE = 3;
/**
 * How many multi-day runs a week row will draw before the rest fall back to
 * the "+N more" line. Past three, the grid stops being a month and starts
 * being a Gantt chart.
 */
const MAX_LANES = 3;
/** A band is exactly a chip tall, and carries a chip's gap under it. */
const BAND_STEP = 21;

export function DesktopMonth({
  onView,
}: {
  onView: (view: "week" | "month") => void;
}): React.JSX.Element {
  const { zone } = useSession();
  const navigate = useNavigate();

  const todayDate = today(zone);
  const [anchor, setAnchor] = useState(() => DateTime.fromISO(todayDate, { zone }).startOf("month").toISODate() ?? todayDate);
  const [selected, setSelected] = useState(todayDate);
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);

  const grid = useMemo(() => {
    const first = DateTime.fromISO(anchor, { zone });
    const lead = first.weekday % 7;
    const gridStart = first.minus({ days: lead });
    const rows = Math.ceil((lead + (first.daysInMonth ?? 30)) / 7);
    return Array.from({ length: rows * 7 }, (_, i) => gridStart.plus({ days: i }));
  }, [anchor, zone]);

  const from = grid[0]?.toISODate() ?? anchor;
  const to = addDays(grid[grid.length - 1]?.toISODate() ?? anchor, 1, zone);

  const load = useCallback(async () => {
    setOccurrences((await api.events(from, to)).occurrences);
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Everything on a day, spanning runs included — what the panel reports. */
  const byDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    for (const o of occurrences ?? []) {
      for (const date of datesCovered(o.starts_at, o.ends_at, zone)) {
        if (!map.has(date)) map.set(date, []);
        map.get(date)?.push(o);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
        return a.starts_at.localeCompare(b.starts_at);
      });
    }
    return map;
  }, [occurrences, zone]);

  /**
   * The grid draws runs and single days differently, so they are separated
   * before it gets there. A run becomes one band across the week; repeating
   * its chip in five cells is what made a week in Denver look like five
   * unrelated trips.
   */
  const spanning = useMemo(
    () =>
      (occurrences ?? []).filter(
        (o) => datesCovered(o.starts_at, o.ends_at, zone).length > 1,
      ),
    [occurrences, zone],
  );

  const singleByDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    for (const [date, list] of byDay) {
      map.set(
        date,
        list.filter((o) => datesCovered(o.starts_at, o.ends_at, zone).length === 1),
      );
    }
    return map;
  }, [byDay, zone]);

  const weeks = useMemo(() => {
    const out: string[][] = [];
    const dates = grid.map((d) => d.toISODate() ?? "");
    for (let i = 0; i < dates.length; i += 7) out.push(dates.slice(i, i + 7));
    return out;
  }, [grid]);

  const month = DateTime.fromISO(anchor, { zone }).month;
  const selectedList = byDay.get(selected) ?? [];
  const selectedDate = DateTime.fromISO(selected, { zone });

  const shift = (months: number): void => {
    setAnchor(DateTime.fromISO(anchor, { zone }).plus({ months }).toISODate() ?? anchor);
  };

  return (
    <div className="dk">
      <DesktopChrome
        title={DateTime.fromISO(anchor, { zone }).toFormat("LLLL yyyy")}
        view="month"
        onView={onView}
        onPrev={() => shift(-1)}
        onNext={() => shift(1)}
        onToday={() => {
          setAnchor(DateTime.fromISO(todayDate, { zone }).startOf("month").toISODate() ?? todayDate);
          setSelected(todayDate);
        }}
      />

      <div className="dk-monthwrap">
        <div className="dk-month">
          <div className="dk-month__dow">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="dk-month__grid">
            {weeks.map((week) => {
              const { bands, lanes } = bandsForRow(spanning, week, zone);
              const shownLanes = Math.min(lanes, MAX_LANES);

              /*
               * A band pushed past the lane limit is only missing from the
               * days it actually covers. Counting them row-wide put "+1 more"
               * on a Sunday whose only event was the band drawn across it.
               */
              const dropped = bands.filter((band) => band.lane >= MAX_LANES);
              const hiddenBandsOn = (column: number): number =>
                dropped.filter((band) => column >= band.from && column <= band.to).length;

              return (
                <div key={week[0] ?? ""} className="dk-month__week">
                  {week.map((date, column) => {
                    const cell = DateTime.fromISO(date, { zone });
                    const list = singleByDay.get(date) ?? [];
                    const outside = cell.month !== month;
                    const hiddenBands = hiddenBandsOn(column);

                    // Bands have already taken their share of the cell, so
                    // what is left is the budget minus the lanes above.
                    const wanted = shownLanes + list.length + hiddenBands;
                    const room = Math.max(
                      0,
                      (wanted <= SLOTS_ALONE ? SLOTS_ALONE : SLOTS_WITH_MORE) - shownLanes,
                    );
                    const overflow = Math.max(0, list.length - room) + hiddenBands;

                    return (
                      <button
                        key={date}
                        type="button"
                        className={[
                          "dk-cell",
                          outside ? "is-outside" : "",
                          date === todayDate ? "is-today" : "",
                          date === selected ? "is-selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => setSelected(date)}
                        onDoubleClick={() => void navigate(`/day/${date}`)}
                      >
                        <span className="dk-cell__num">{cell.day}</span>

                        {/* Holds open the height the bands overhead are
                            using, so the chips start below them. */}
                        <span
                          className="dk-cell__lanes"
                          style={{ height: shownLanes * BAND_STEP }}
                          aria-hidden="true"
                        />

                        {list.slice(0, room).map((o) => {
                          const lead = o.people[0];
                          return (
                            <span
                              key={o.occurrence_id}
                              className="dk-cell__chip"
                              style={{
                                background:
                                  lead === undefined ? "var(--paper)" : tintOf(lead.color),
                                borderLeftColor: lead?.color ?? "var(--mid)",
                                opacity: o.is_background ? 0.6 : 1,
                              }}
                            >
                              {o.title}
                            </span>
                          );
                        })}

                        {overflow > 0 && (
                          <span className="dk-cell__more">+{overflow} more</span>
                        )}
                      </button>
                    );
                  })}

                  <div className="dk-month__bands">
                    {bands
                      .filter((band) => band.lane < MAX_LANES)
                      .map((band) => {
                        const lead = band.item.people[0];
                        return (
                          <span
                            key={`${band.item.occurrence_id}:${week[0] ?? ""}`}
                            className={[
                              "dk-band",
                              band.clippedStart ? "is-clipped-start" : "",
                              band.clippedEnd ? "is-clipped-end" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            title={spanAria(band.item, week[band.from] ?? "", zone)}
                            style={{
                              gridColumn: `${String(band.from + 1)} / ${String(band.to + 2)}`,
                              gridRow: band.lane + 1,
                              background:
                                lead === undefined ? "var(--paper)" : tintOf(lead.color),
                              borderLeftColor: lead?.color ?? "var(--mid)",
                              opacity: band.item.is_background ? 0.6 : 1,
                            }}
                          >
                            {/* Only the day it starts on gets the title. A
                                run repeating its own name every seven days is
                                the noise bands exist to remove; where it is
                                cut off by the row, the leading ellipsis says
                                the sentence began last week. */}
                            <span className="dk-band__title">
                              {band.clippedStart ? `… ${band.item.title}` : band.item.title}
                            </span>
                          </span>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="dk-panel">
          <h2>{selectedDate.toFormat("cccc d")}</h2>
          <p className="dk-panel__count">
            {selectedList.length === 0
              ? selectedDate.toFormat("LLLL")
              : `${String(selectedList.length)} ${selectedList.length === 1 ? "thing" : "things"} · ${selectedDate.toFormat("LLLL")}`}
          </p>

          {selectedList.length === 0 ? (
            <div className="dk-panel__empty">
              <p className="dk-panel__emptyhead">Nothing that day</p>
              <p>Click a square to plan something.</p>
            </div>
          ) : (
            <div className="dk-panel__list">
              {selectedList.map((o) => {
                const lead = o.people[0];
                return (
                  <button
                    key={o.occurrence_id}
                    type="button"
                    className="dk-panel__row"
                    style={{
                      background: lead === undefined ? "var(--paper)" : tintOf(lead.color),
                      borderLeftColor: lead?.color ?? "var(--mid)",
                    }}
                    onClick={() =>
                      void navigate(
                        `/event/${o.event_id}?occ=${encodeURIComponent(o.occurrence_id)}`,
                      )
                    }
                  >
                    <span className="dk-panel__who">
                      {o.people.map((p) => (
                        <span
                          key={p.id}
                          className="disc"
                          style={{
                            width: 18,
                            height: 18,
                            fontSize: 10,
                            background: p.color,
                            color: foregroundOn(p.color),
                          }}
                        >
                          {initialOf(p.display_name)}
                        </span>
                      ))}
                    </span>
                    <span className="dk-panel__title">{o.title}</span>
                    <span className="dk-panel__meta">
                      {spanMeta(o, selected, zone)}
                      {o.location === null ? "" : ` · ${o.location}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
